// src/components/Planner/PlannerViews/KanbanView.jsx
import React, { useMemo, useState, useRef, useEffect } from "react";
import { usePlanner } from "../../../contexts/PlannerContext";
import TaskEditorModal from "./TaskEditorModal";

/* ------------ Stages / tags ------------ */
const STAGES = [
  { key: "forming",     label: "Forming" },
  { key: "drying",      label: "Drying" },
  { key: "bisque",      label: "Bisque" },
  { key: "glazing",     label: "Glazing" },
  { key: "glazeFiring", label: "Glaze Firing" },
  { key: "qcPack",      label: "QC & Pack" },
  { key: "dispatch",    label: "Dispatch" },
  { key: "other",       label: "Other" },
];
const STAGE_KEYS = new Set(STAGES.map(s => s.key));

/* ------------ Utils ------------ */
const shortDateTime = (iso) => {
  try {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString([], { month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" });
  } catch { return ""; }
};
const newId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `pln_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

function firstStageTag(tags) {
  return (Array.isArray(tags) ? tags : []).find(t => STAGE_KEYS.has(t)) || "other";
}
function ensureStageTag(nextTags, targetKey) {
  const base = Array.isArray(nextTags) ? [...nextTags] : [];
  const filtered = base.filter(t => !STAGE_KEYS.has(t)); // strip any existing stage tag
  if (!filtered.includes(targetKey)) filtered.push(targetKey);
  return filtered;
}
function stageColor(stage) {
  const m = {
    forming:"#06b6d4", drying:"#22c55e", bisque:"#f59e0b", glazing:"#a78bfa",
    glazeFiring:"#ef4444", qcPack:"#14b8a6", dispatch:"#64748b", other:"#9ca3af",
  };
  return m[stage] || "#9ca3af";
}

/* ==================================== */
export default function KanbanView({
  events = [],
  openEventId = null,
  onDeepLinkHandled,
}) {
  const planner = usePlanner();
  const { upsertEvents } = planner;
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (!openEventId) return;
    const match = (events || []).find((ev) => String(ev.id) === String(openEventId));
    if (match) {
      setEditing(match);
      onDeepLinkHandled?.(true);
    } else if (events?.length >= 0) {
      onDeepLinkHandled?.(false);
    }
  }, [openEventId, events, onDeepLinkHandled]);

  /* quick-add text per column */
  const [quick, setQuick] = useState(() => Object.fromEntries(STAGES.map(s => [s.key, ""])));

  /* drag visual state */
  const [draggingId, setDraggingId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const [overCard, setOverCard] = useState({ col: null, id: null });

  /* optimistic delete: keep hidden ids locally */
  const [hiddenIds, setHiddenIds] = useState(() => new Set());

  /* theme tokens */
  const isDark =
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark");

  const t = isDark
    ? {
        panelBg: "transparent",
        colBg: "rgba(255,255,255,.04)",
        colBorder: "rgba(255,255,255,.12)",
        headBg: "rgba(255,255,255,.06)",
        headText: "rgba(255,255,255,.92)",
        cardBg: "rgba(0,0,0,.60)",
        cardBorder: "rgba(255,255,255,.12)",
        cardText: "#fff",
        subText: "rgba(255,255,255,.75)",
        chipBg: "rgba(255,255,255,.10)",
        chipBorder: "rgba(255,255,255,.15)",
        doneBg: "rgba(34,197,94,.15)",
        doneText: "#22c55e",
        doneBorder: "rgba(34,197,94,.35)",
        emptyText: "rgba(255,255,255,.45)",
        dropBg: "rgba(59,130,246,.20)",
        hoverBg: "rgba(255,255,255,.06)",
      }
    : {
        panelBg: "transparent",
        colBg: "#f8fafc",
        colBorder: "#e5e7eb",
        headBg: "#eef2f7",
        headText: "#0b1220",
        cardBg: "#ffffff",
        cardBorder: "#e5e7eb",
        cardText: "#0f172a",
        subText: "#334155",
        chipBg: "#f1f5f9",
        chipBorder: "#e2e8f0",
        doneBg: "rgba(34,197,94,.12)",
        doneText: "#15803d",
        doneBorder: "rgba(34,197,94,.30)",
        emptyText: "#64748b",
        dropBg: "rgba(59,130,246,.12)",
        hoverBg: "rgba(15,23,42,.04)",
      };

  /* prevent text selection while dragging for nicer UX */
  useEffect(() => {
    if (!draggingId) return;
    const prev = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    return () => { document.body.style.userSelect = prev; };
  }, [draggingId]);

  /* derive events source: prop override or planner context */
  const sourceEvents = useMemo(() => {
    const ctxEvents = planner?.events || [];
    if (Array.isArray(events) && events.length > 0) return events;
    return ctxEvents;
  }, [events, planner?.events]);

  /* visible events after optimistic hides */
  const visibleEvents = useMemo(
    () => (sourceEvents || []).filter(e => !hiddenIds.has(String(e.id))),
    [sourceEvents, hiddenIds]
  );

  /* maps for fast lookup */
  const idMap = useMemo(() => {
    const m = new Map();
    for (const e of visibleEvents) m.set(String(e.id), e);
    return m;
  }, [visibleEvents]);

  /* group + sort by stage, then by order (fallback start) */
  const grouped = useMemo(() => {
    const map = new Map(STAGES.map(s => [s.key, []]));
    (visibleEvents || []).forEach(e => {
      const key = firstStageTag(e.tags) || String(e.stage || "").toLowerCase();
      const safeKey = STAGES.some(s => s.key === key) ? key : "other";
      map.get(safeKey).push(e);
    });
    for (const arr of map.values()) {
      arr.sort((a, b) => {
        const ao = a.order ?? Number.MAX_SAFE_INTEGER;
        const bo = b.order ?? Number.MAX_SAFE_INTEGER;
        if (ao !== bo) return ao - bo;
        return new Date(a.start || 0) - new Date(b.start || 0);
      });
    }
    return map;
  }, [visibleEvents]);

  /* reindex helper */
  function reindexColumn(list, colKey) {
    return list.map((ev, i) => ({
      ...ev,
      tags: ensureStageTag(ev.tags, colKey),
      order: i + 1,
    }));
  }

  const quickAdd = (colKey, openEditor = false) => {
    const title = (quick[colKey] || "").trim();
    if (!title) return;
    const start = new Date();
    const end = new Date(start.getTime() + 60 * 60000);
    const colList = grouped.get(colKey) || [];
    const nextOrder = (colList.length ? (Math.max(...colList.map(e => e.order || 0)) + 1) : 1);

    const ev = {
      id: newId(),
      title,
      start: start.toISOString(),
      end: end.toISOString(),
      status: "todo",
      progress: 0,
      tags: ensureStageTag([], colKey),
      order: nextOrder,
    };
    upsertEvents([ev]);
    setQuick((q) => ({ ...q, [colKey]: "" }));
    if (openEditor) setEditing(ev);
  };

  const markDone = (ev) => {
    upsertEvents([{ ...ev, status: "done", progress: 100 }]);
  };

  /* robust + optimistic delete or archive */
  async function handleDelete(ev) {
    const id = String(ev?.id || "");
    if (!id) return;
    const ok = window.confirm("Delete this task?");
    if (!ok) return;

    // hide optimistically
    setHiddenIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });

    try {
      if (typeof planner.archiveEvent === "function") {
        await planner.archiveEvent(id);
        return;
      }
      if (typeof planner.deleteEvent === "function") {
        await planner.deleteEvent(id);
        return;
      }
      if (typeof planner.deleteEvents === "function") {
        await planner.deleteEvents([id]);
        return;
      }
      if (typeof planner.setEvents === "function") {
        planner.setEvents((prev = []) => prev.filter(x => String(x.id) !== id));
        return;
      }
      upsertEvents([{ id, _deleted: true }]);
    } catch (e) {
      console.error("[KanbanView] delete failed:", e);
      // rollback hide on failure
      setHiddenIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  /* drag handlers (card) */
  const onDragStart = (evObj, ev) => {
    setDraggingId(String(evObj.id));
    ev.dataTransfer.setData("text/plain", String(evObj.id));
    ev.dataTransfer.effectAllowed = "move";
  };
  const onDragEnd = () => {
    setDraggingId(null);
    setOverCol(null);
    setOverCard({ col: null, id: null });
  };

  /* column drop handlers */
  const onColDragEnter = (key, e) => {
    e.preventDefault();
    setOverCol(key);
  };
  const onColDragOver = (key, e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setOverCol(key);
  };
  const onColDrop = (key, e) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    const dragged = idMap.get(id);
    if (!dragged) return;

    const currentColKey = firstStageTag(dragged.tags);
    const baseList = (grouped.get(key) || []).filter(ev => String(ev.id) !== id);

    // insertion index (at end unless hovering a card)
    let insertIndex = baseList.length;
    if (overCard.col === key && overCard.id) {
      const idx = baseList.findIndex(ev => String(ev.id) === String(overCard.id));
      if (idx >= 0) insertIndex = idx;
    }

    const newDragged = { ...dragged, tags: ensureStageTag(dragged.tags, key) };
    baseList.splice(insertIndex, 0, newDragged);

    const patches = reindexColumn(baseList, key);

    if (currentColKey !== key) {
      const srcList = (grouped.get(currentColKey) || []).filter(ev => String(ev.id) !== id);
      patches.push(...reindexColumn(srcList, currentColKey));
    }

    upsertEvents(patches);
    setOverCol(null);
    setOverCard({ col: null, id: null });
    setDraggingId(null);
  };

  /* card hover (to compute insertion point) */
  const onCardDragOver = (colKey, cardId, e) => {
    e.preventDefault();
    setOverCol(colKey);
    setOverCard({ col: colKey, id: cardId });
  };

  return (
    <div
      className="grid md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-3"
      style={{ background: t.panelBg }}
      role="list"
      aria-label="Kanban columns"
    >
      {STAGES.map((col) => {
        const list = grouped.get(col.key) || [];
        const isOver = overCol === col.key;
        return (
          <div
            key={col.key}
            className="rounded-xl overflow-hidden flex flex-col"
            style={{
              border: `1px solid ${t.colBorder}`,
              background: t.colBg,
              outline: isOver ? `2px dashed ${stageColor(col.key)}` : "none",
              outlineOffset: isOver ? 2 : 0,
            }}
            onDragEnter={(e) => onColDragEnter(col.key, e)}
            onDragOver={(e) => onColDragOver(col.key, e)}
            onDrop={(e) => onColDrop(col.key, e)}
            role="region"
            aria-label={`${col.label} column`}
          >
            {/* column header */}
            <div
              className="px-3 py-2 text-sm flex items-center justify-between"
              style={{ background: t.headBg, color: t.headText }}
            >
              <span className="font-semibold">{col.label}</span>
              <span className="opacity-70">{list.length}</span>
            </div>

            {/* quick add */}
            <div className="p-2 border-b" style={{ borderColor: t.colBorder }}>
              <div className="flex gap-2">
                <input
                  className="px-2 py-1 rounded text-sm w-full"
                  placeholder={`Add to ${col.label}`}
                  value={quick[col.key] || ""}
                  onChange={(e) => setQuick({ ...quick, [col.key]: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) quickAdd(col.key, true);
                    else if (e.key === "Enter") quickAdd(col.key, false);
                  }}
                  style={{ background: t.cardBg, color: t.cardText, border: `1px solid ${t.cardBorder}` }}
                />
                <button
                  onClick={() => quickAdd(col.key)}
                  className="px-2 py-1 rounded text-xs"
                  style={{ background: "#10b981", color: "#fff" }}
                  title={`Add to ${col.label}`}
                >
                  Add
                </button>
              </div>
            </div>

            {/* cards */}
            <div
              className="p-2 space-y-2 min-h-[160px]"
              style={{ background: isOver ? t.dropBg : "transparent" }}
              role="list"
            >
              {list.length === 0 ? (
                <div
                  className="text-xs text-center py-10 rounded"
                  style={{ color: t.emptyText, border: `1px dashed ${t.colBorder}` }}
                >
                  No tasks
                  <div className="opacity-60">Drop tasks here to move</div>
                </div>
              ) : (
                list.map((evObj) => {
                  const st = firstStageTag(evObj.tags);
                  const isDragging = String(evObj.id) === draggingId;
                  const isOverCard = overCard.col === col.key && String(overCard.id) === String(evObj.id);

                  return (
                    <div key={evObj.id}>
                      {/* visual placeholder when hovering 'before' this card */}
                      {isOverCard && (
                        <div className="h-3 rounded mb-1" style={{ background: stageColor(col.key), opacity: 0.25 }} />
                      )}

                      <div
                        role="button"
                        aria-label={`Task: ${evObj.title || "Untitled"}`}
                        draggable
                        onDragStart={(e) => onDragStart(evObj, e)}
                        onDragEnd={onDragEnd}
                        onDragOver={(e) => onCardDragOver(col.key, evObj.id, e)}
                        onClick={() => setEditing(evObj)}
                        className="rounded-lg p-2 cursor-pointer transition-colors"
                        style={{
                          opacity: isDragging ? 0.5 : 1,
                          background: t.cardBg,
                          border: `1px solid ${t.cardBorder}`,
                          color: t.cardText,
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = t.hoverBg)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = t.cardBg)}
                      >
                        {/* Title */}
                        <div className="text-sm font-semibold truncate">{evObj.title || "(untitled)"}</div>

                        {/* When */}
                        <div className="text-xs mt-0.5" style={{ color: t.subText }}>
                          {shortDateTime(evObj.start)}
                        </div>

                        {/* Meta chips */}
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          {evObj.category ? (
                            <span
                              className="px-2 py-0.5 rounded text-[11px]"
                              style={{ background: t.chipBg, border: `1px solid ${t.chipBorder}` }}
                            >
                              {evObj.category}
                            </span>
                          ) : null}
                          {evObj.assigneeName || evObj.assigneeId || evObj.assignee ? (
                            <span
                              className="px-2 py-0.5 rounded text-[11px]"
                              style={{ background: t.chipBg, border: `1px solid ${t.chipBorder}` }}
                            >
                              {evObj.assigneeName || evObj.assignee || evObj.assigneeId}
                            </span>
                          ) : null}
                          <span
                            className="px-2 py-0.5 rounded text-[11px]"
                            style={{
                              color: stageColor(st),
                              border: `1px solid ${stageColor(st)}`,
                              background: "transparent",
                            }}
                          >
                            {STAGES.find(s => s.key === st)?.label || col.label}
                          </span>
                          {evObj.color ? (
                            <span
                              className="inline-block w-3 h-3 rounded"
                              title="Task color"
                              style={{ background: evObj.color, border: "1px solid rgba(0,0,0,.12)" }}
                            />
                          ) : null}
                        </div>

                        {/* Actions */}
                        <div className="mt-2 flex gap-2 justify-end">
                          <button
                            onClick={(e) => { e.stopPropagation(); markDone(evObj); }}
                            className="px-2 py-1 rounded text-xs"
                            style={{ background: t.doneBg, color: t.doneText, border: `1px solid ${t.doneBorder}` }}
                          >
                            Done
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(evObj); }}
                            className="px-2 py-1 rounded text-xs"
                            style={{ background: "rgba(239,68,68,.14)", color:"#ef4444", border:"1px solid rgba(239,68,68,.35)" }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );
      })}

      {/* Editor modal (shared) */}
      <TaskEditorModal open={!!editing} event={editing || undefined} onClose={() => setEditing(null)} />
    </div>
  );
}
